import * as React from 'react';
import { Alert, Button, Checkbox, Form, Input, Modal, Select } from 'antd';
import { observer } from 'mobx-react-lite';
import { DateTime } from 'luxon';
import DatePicker from '../../../components/DatePicker';
import type { ApiKeysStore } from '../stores/ApiKeysStore';
import type { ApiKeyInput, CreateApiKeyInput } from '../types';

type Props = {
    store: ApiKeysStore;
};

type ApiKeyFormValues = Omit<CreateApiKeyInput, 'expiresAt'> & { expiresAt?: Date | null };

const toFormValues = (store: ApiKeysStore): ApiKeyFormValues => {
    const apiKey = store.editingApiKey;
    const expiry = apiKey?.expiresAt ? DateTime.fromISO(apiKey.expiresAt).toJSDate() : null;

    return {
        name: apiKey?.name ?? '',
        description: apiKey?.description ?? '',
        integrationName: apiKey?.integrationName ?? '',
        scopes: apiKey?.scopes ?? [],
        allowedProjectIds: apiKey?.allowedProjectIds ?? [],
        enabled: apiKey?.enabled ?? true,
        expiresAt: expiry && Number.isFinite(expiry.getTime()) ? expiry : null
    };
};

const toApiKeyInput = (values: ApiKeyFormValues): ApiKeyInput => ({
    name: values.name.trim(),
    description: values.description.trim(),
    integrationName: values.integrationName.trim(),
    scopes: values.scopes,
    allowedProjectIds: values.allowedProjectIds,
    expiresAt: values.expiresAt ? DateTime.fromJSDate(values.expiresAt).toISO() : null
});

const ApiKeyEditorModal: React.FC<Props> = ({ store }) => {
    const [form] = Form.useForm<ApiKeyFormValues>();

    React.useEffect(() => {
        if (!store.isEditorVisible) {
            return;
        }

        form.setFieldsValue(toFormValues(store));
    }, [form, store, store.editingApiKey, store.isEditorVisible]);

    const closeEditor = () => {
        store.closeEditor();
    };

    const submitEditor = async () => {
        let values: ApiKeyFormValues;

        try {
            values = await form.validateFields();
        } catch {
            return;
        }

        const input = toApiKeyInput(values);
        const editingApiKey = store.editingApiKey;

        if (editingApiKey) {
            await store.updateApiKey(editingApiKey.id, input);
            return;
        }

        await store.createApiKey({ ...input, enabled: values.enabled });
    };

    const validateExpiry = (_: unknown, value?: Date | null) => {
        if (!value) {
            return Promise.resolve();
        }

        const expiry = DateTime.fromJSDate(value);
        const existingExpiry = store.editingApiKey?.expiresAt;
        const isUnchanged = existingExpiry && expiry.toMillis() === DateTime.fromISO(existingExpiry).toMillis();

        if (!expiry.isValid || (!isUnchanged && expiry.toMillis() <= Date.now())) {
            return Promise.reject(new Error('Expiry must be a future date and time.'));
        }

        return Promise.resolve();
    };

    const isEditing = !!store.editingApiKey;

    return (
        <Modal
            className="alpha-portal-dialog"
            title={isEditing ? 'Edit API key' : 'Create API key'}
            visible={store.isEditorVisible}
            onCancel={closeEditor}
            maskClosable={false}
            closable={false}
            keyboard={!store.isBusy}
            forceRender
            destroyOnClose
            afterClose={form.resetFields}
            centered
            footer={[
                <Button key="cancel" size="large" className="light" disabled={store.isBusy} onClick={closeEditor}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    size="large"
                    loading={store.isSaving}
                    disabled={store.isBusy}
                    onClick={submitEditor}
                >
                    {isEditing ? 'Save changes' : 'Create API key'}
                </Button>
            ]}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label="Key name"
                    name="name"
                    rules={[{ required: true, whitespace: true, message: 'Enter a name for this API key.' }]}
                >
                    <Input disabled={store.isBusy} />
                </Form.Item>
                <Form.Item label="Description" name="description">
                    <Input.TextArea rows={3} disabled={store.isBusy} />
                </Form.Item>
                <Form.Item
                    label="Integration name"
                    name="integrationName"
                    rules={[{ required: true, whitespace: true, message: 'Enter the integration name.' }]}
                    extra="Identifies the integration that will use this key."
                >
                    <Input disabled={store.isBusy} />
                </Form.Item>
                <Form.Item
                    label="Permissions / scopes"
                    name="scopes"
                    rules={[{ required: true, type: 'array', min: 1, message: 'Select at least one scope.' }]}
                >
                    <Select
                        disabled={store.isBusy}
                        mode="multiple"
                        placeholder="Select scopes"
                        options={store.scopes.map(scope => ({
                            value: scope.scope,
                            label: `${scope.scope} — ${scope.description}`
                        }))}
                    />
                </Form.Item>
                <Form.Item label="Allowed projects" name="allowedProjectIds">
                    <Select
                        disabled={store.isBusy}
                        mode="multiple"
                        placeholder="Select projects"
                        showSearch
                        optionFilterProp="label"
                        options={store.projects.map(project => ({ value: project.id, label: project.name }))}
                    />
                </Form.Item>
                <Form.Item
                    noStyle
                    shouldUpdate={(previous, current) => previous.allowedProjectIds !== current.allowedProjectIds}
                >
                    {({ getFieldValue }) =>
                        (getFieldValue('allowedProjectIds') as string[] | undefined)?.length ? null : (
                            <Alert
                                type="warning"
                                showIcon
                                message="No projects selected: this API key will have no project access."
                                style={{ marginBottom: 24 }}
                            />
                        )
                    }
                </Form.Item>
                <Form.Item label="Expires at" name="expiresAt" rules={[{ validator: validateExpiry }]}>
                    <DatePicker
                        disabled={store.isBusy}
                        showTime
                        showNow={false}
                        format="yyyy-MM-dd HH:mm"
                        style={{ width: '100%' }}
                    />
                </Form.Item>
                {!isEditing && (
                    <Form.Item name="enabled" valuePropName="checked">
                        <Checkbox disabled={store.isBusy}>Enabled</Checkbox>
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};

export default observer(ApiKeyEditorModal);
